import jsPDF from 'jspdf';

class PDFInvoiceService {
  constructor() {
    this.pdf = null;
    this.logoImage = null;
    this.logoLoaded = false;
    this.LUT_NUMBER = 'AD240925013110O';
  }

  // Generate PDF invoice for a transaction
  async generateInvoice(transactionData, userData = {}) {
    try {
      // Create new PDF document
      this.pdf = new jsPDF();
      
      // Set up the document
      this.setupDocument();
      
      // Load logo
      await this.loadLogo();
      
      // Add header with logo
      await this.addHeader();
      
      // Add company info
      this.addCompanyInfo();
      
      // Add invoice details with LUT (if needed)
      this.addInvoiceDetails(transactionData);
      
      // Add user info
      this.addUserInfo(userData);
      
      // Add transaction details
      this.addTransactionDetails(transactionData);
      
      // Add footer
      this.addFooter();
      
      return this.pdf;
    } catch (error) {
      console.error('Error generating PDF invoice:', error);
      throw error;
    }
  }

  // Load logo image
  async loadLogo() {
    try {
      // Load the logo SVG file
      const logoPath = '/octalchiplogo.svg';
      
      // Convert SVG to an image that jsPDF can use
      const response = await fetch(logoPath);
      if (response.ok) {
        const svgText = await response.text();
        
        // Create an image element to load the SVG
        return new Promise((resolve) => {
          const img = new Image();
          let blobUrl = null;
          
          img.onload = () => {
            try {
              // Create a canvas to convert SVG to PNG
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Set canvas dimensions (width/height from the loaded SVG)
              canvas.width = img.width || 1041;
              canvas.height = img.height || 194;
              
              // Draw the image to canvas
              ctx.drawImage(img, 0, 0);
              
              // Convert canvas to data URL
              this.logoImage = canvas.toDataURL('image/png');
              this.logoLoaded = true;
              
              // Clean up blob URL
              if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
              }
              resolve();
            } catch (error) {
              console.warn('Error converting SVG to image:', error);
              this.logoLoaded = false;
              if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
              }
              resolve();
            }
          };
          
          img.onerror = () => {
            console.warn('Error loading logo image');
            this.logoLoaded = false;
            if (blobUrl) {
              URL.revokeObjectURL(blobUrl);
            }
            resolve();
          };
          
          // Convert SVG text to a data URL
          const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
          blobUrl = URL.createObjectURL(svgBlob);
          img.src = blobUrl;
        });
      } else {
        console.warn('Logo image not found, continuing without logo');
        this.logoLoaded = false;
      }
    } catch (error) {
      console.warn('Error loading logo:', error);
      this.logoLoaded = false;
    }
  }

  setupDocument() {
    // Set document properties
    this.pdf.setProperties({
      title: 'Invoice',
      subject: 'Email Validation Service Invoice',
      author: 'Email Validator',
      creator: 'Email Validator System'
    });
    
    // Set default font
    this.pdf.setFont('helvetica');
  }

  async addHeader() {
    // Add logo at top right if loaded
    if (this.logoLoaded && this.logoImage) {
      try {
        // Add logo at top right (approx 110px from left, 20px from top)
        // Logo dimensions: 80x15mm to fit properly at top right
        // SVG has aspect ratio of ~1041:194 (roughly 5.4:1)
        this.pdf.addImage(this.logoImage, 'SVG', 110, 20, 80, 15);
      } catch (error) {
        console.warn('Error adding logo image:', error);
      }
    }
    
    // Add logo/company name
    this.pdf.setFontSize(24);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Email Validator', 20, 30);
    
    // Add tagline
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Professional Email Validation Services', 20, 38);
    
    // Add line separator
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.line(20, 45, 190, 45);
  }

  addCompanyInfo() {
    const companyInfo = [
      'Octalchip',
      '404, Opera point, Kothariya,',
      'Rajkot, Gujarat, 360022',
      'India',
      '',
      'Email: business@octalchip.com',
      'Phone: +91 7574082582',
      'Website: https://mailvalidator.octalchip.com'
    ];

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    
    companyInfo.forEach((line, index) => {
      this.pdf.text(line, 20, 60 + (index * 5));
    });
  }

  addInvoiceDetails(transactionData) {
    // Invoice title
    this.pdf.setFontSize(18);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('INVOICE', 115, 60);
    
    // Check if this is a non-Indian transaction (USD payment)
    const isNonIndianTransaction = transactionData.currency === 'USD';
    
    // Invoice details
    const invoiceDetails = [
      `Invoice #: ${transactionData.invoice || transactionData.id}`,
      `Date: ${this.formatDate(transactionData.date)}`,
      `Status: ${this.formatStatus(transactionData.status)}`,
      `Payment Method: ${this.formatPaymentMethod(transactionData.paymentMethod)}`
    ];

    // Add LUT number only for non-Indian transactions (USD)
    if (isNonIndianTransaction) {
      invoiceDetails.push(`LUT Number: ${this.LUT_NUMBER}`);
    }

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    
    invoiceDetails.forEach((line, index) => {
      this.pdf.text(line, 115, 70 + (index * 5));
    });
  }

  addUserInfo(userData) {
    if (!userData.email) return;

    // Bill to section
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Bill To:', 20, 105);
    
    const userInfo = [
      userData.fullName || 'User',
      userData.email,
      userData.companyName || ''
    ].filter(Boolean);

    // Add address if available
    if (userData.address) {
      const formattedAddress = this.formatAddress(userData.address);
      if (formattedAddress) {
        // Split address by newlines (already formatted properly)
        const addressLines = formattedAddress.split('\n').filter(line => line.trim());
        userInfo.push(...addressLines);
      }
    }

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    
    userInfo.forEach((line, index) => {
      this.pdf.text(line, 20, 110 + (index * 5));
    });
  }

  addTransactionDetails(transactionData) {
    // Calculate dynamic start position based on user info length
    const userInfoLines = this.calculateUserInfoLines();
    const startY = 140 + (userInfoLines * 5);
    
    // Transaction details table
    this.pdf.setFillColor(240, 240, 240);
    this.pdf.rect(20, startY, 170, 10, 'F');
    
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Description', 25, startY + 7);
    this.pdf.text('Amount', 150, startY + 7);
    
    // Table content
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(transactionData.description, 25, startY + 17);
    
    // Format amount with currency
    const amount = this.formatCurrency(transactionData.amount, transactionData.currency);
    this.pdf.text(amount, 150, startY + 17);
    
    // Total line
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Total:', 130, startY + 27);
    this.pdf.text(amount, 150, startY + 27);
    
    // Add line separator
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.line(20, startY + 32, 190, startY + 32);
  }

  calculateUserInfoLines() {
    // This is a helper method to calculate how many lines the user info will take
    // We'll estimate based on typical user data
    return 4; // Default: name, email, company, address (if present)
  }

  addFooter() {
    const footerY = 280;
    
    // Terms and conditions
    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'normal');
    
    const terms = [
      'Terms & Conditions:',
      '• Payment is due within 30 days of invoice date',
      '• Late payments may incur additional charges',
      '• All services are provided as per our terms of service',
      '• For support, contact us at support@emailvalidator.com'
    ];
    
    terms.forEach((line, index) => {
      this.pdf.text(line, 20, footerY + (index * 4));
    });
    
    // Page number
    this.pdf.setFontSize(8);
    this.pdf.text('Page 1 of 1', 170, 290);
  }

  // Helper methods
  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  formatCurrency(amount, currency) {
    if (currency === 'INR') {
      return `Rs. ${amount.toFixed(2)}`;
    } else if (currency === 'USD') {
      return `$${amount.toFixed(2)}`;
    } else {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  formatStatus(status) {
    const statusMap = {
      'paid': 'Paid',
      'completed': 'Completed',
      'pending': 'Pending',
      'failed': 'Failed',
      'refunded': 'Refunded'
    };
    return statusMap[status] || status;
  }

  formatPaymentMethod(method) {
    const methodMap = {
      'razorpay': 'Razorpay',
      'stripe': 'Stripe',
      'card': 'Credit Card',
      'bank_transfer': 'Bank Transfer',
      'paypal': 'PayPal',
      'null': 'Not Specified'
    };
    return methodMap[method] || method;
  }

  formatAddress(address) {
    if (!address) return '';
    
    if (typeof address === 'string') {
      return address.trim();
    }
    
    if (typeof address === 'object') {
      // Use the exact address format from Profile section
      const addressParts = [];
      
      // Street address
      if (address.street && address.street.trim()) {
        addressParts.push(address.street.trim());
      }
      
      // City, State, ZIP Code
      const cityStateZip = [];
      if (address.city && address.city.trim()) {
        cityStateZip.push(address.city.trim());
      }
      if (address.state && address.state.trim()) {
        cityStateZip.push(address.state.trim());
      }
      if (address.zipCode && address.zipCode.trim()) {
        cityStateZip.push(address.zipCode.trim());
      }
      
      if (cityStateZip.length > 0) {
        addressParts.push(cityStateZip.join(', '));
      }
      
      // Country
      if (address.country && address.country.trim()) {
        addressParts.push(address.country.trim());
      }
      
      return addressParts.join('\n');
    }
    
    return '';
  }

  splitAddressLines(address, maxLength = 50) {
    if (!address) return [];
    
    const words = address.split(', ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if (currentLine.length + word.length + 2 <= maxLength) {
        currentLine += (currentLine ? ', ' : '') + word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  // Download the PDF
  downloadPDF(filename = 'invoice.pdf') {
    if (!this.pdf) {
      throw new Error('No PDF generated');
    }
    
    this.pdf.save(filename);
  }

  // Get PDF as blob for other uses
  getPDFBlob() {
    if (!this.pdf) {
      throw new Error('No PDF generated');
    }
    
    return this.pdf.output('blob');
  }

  // Get PDF as data URL
  getPDFDataURL() {
    if (!this.pdf) {
      throw new Error('No PDF generated');
    }
    
    return this.pdf.output('dataurlstring');
  }
}

export default new PDFInvoiceService();
